import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Card, Col, Row, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import PageHeader from 'components/common/PageHeader';
import InputDatePicker from 'components/common/InputDatePicker';
import FullPageSpinner from 'components/common/full-page-spinner';
import { getOtifChart, exportOtifLineItems } from 'services/supplier/supplierOtifService';
import { searchSuppliers } from 'services/supplier/supplierDashboardService';
import OtifLineChart from './components/OtifLineChart';
import MonthlyBreakdownTable from './components/MonthlyBreakdownTable';

const SectionTitle = ({ children }) => (
  <h6 style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, borderBottom: '2px solid #2c7be5', paddingBottom: 4 }}>
    {children}
  </h6>
);

SectionTitle.propTypes = {
  children: PropTypes.node
};

const SupplierOtif = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [months, setMonths] = useState([]);
  const [otifPercents, setOtifPercents] = useState([]);

  const supplierSelectOptions = useMemo(
    () =>
      suppliers.map(supplier => ({
        value: supplier.supplierId,
        label: `${supplier.supplierCode} - ${supplier.supplierName}`
      })),
    [suppliers]
  );
  const selectedSupplier =
    supplierSelectOptions.find(
      option => String(option.value) === String(id)
    ) || null;

  useEffect(() => {
    const loadSuppliers = async () => {
      setLoadingSuppliers(true);
      const res = await searchSuppliers({
        pageNum: 1,
        pageSize: 1000,
        isLike: true,
        isGetAll: true,
        keyword: ''
      });
      setSuppliers(res?.items || []);
      setLoadingSuppliers(false);
    };
    loadSuppliers();
  }, []);

  useEffect(() => {
    const loadChart = async () => {
      setLoading(true);
      const res = await getOtifChart(id);
      const rows = res?.data ?? [];
      setMonths(rows.map(r => moment({ year: r.year, month: r.month - 1 }).format('MMM-YY')));
      setOtifPercents(rows.map(r => r.otifPercent));
      setLoading(false);
    };
    loadChart();
  }, [id]);

  const handleDateChange = (col, val) => {
    if (col === 'fromDate') setFromDate(val);
    if (col === 'toDate') setToDate(val);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const from = fromDate.toLocaleDateString('en-CA'); // "2026-01-01"
      const to = toDate.toLocaleDateString('en-CA'); // "2026-01-02"
      const fileName = `OTIF_LineItems_${from.replaceAll('-', '')}_${to.replaceAll('-', '')}.xlsx`;
      await exportOtifLineItems(from, to, id, fileName);
    } finally {
      setExporting(false);
    }
  };

  const isExportDisabled = !fromDate || !toDate || exporting;

  return (
    <>
      <PageHeader
        title="Supplier OTIF"
        description="Purchasing overdue analysis and OTIF performance"
        className="mb-3"
      />

      <Row className="g-3 mb-3">
        <Col lg={12}>
          <Card body>
            <Row>
              <Col xs={12} sm={6} md={4}>
                <Form.Group controlId="supplier-otif-supplier">
                  <Form.Label>Supplier</Form.Label>
                  <Select
                    inputId="supplier-otif-supplier"
                    isLoading={loadingSuppliers}
                    isDisabled={loadingSuppliers}
                    options={supplierSelectOptions}
                    value={selectedSupplier}
                    onChange={option =>
                      option && navigate(`/supplier/supplier-otif/${option.value}`)
                    }
                    placeholder="Select supplier..."
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <FullPageSpinner show={loading} />

      {!loading && (
        <>
          {/* Stock items OTIF - monthly table + chart */}
          <Row className="g-3 mb-3">
            <Col lg={12}>
              <Card body>
                <SectionTitle>Last 12 Months: Stock Items</SectionTitle>
                <div style={{ overflowX: 'auto' }}>
                  <MonthlyBreakdownTable months={months} data={otifPercents} />
                </div>
                <OtifLineChart months={months} data={otifPercents} title="Stock Items OTIF %" />
              </Card>
            </Col>
          </Row>

          {/* Export line items */}
          <Row className="g-3 mb-3">
            <Col lg={12}>
              <Card body>
                <SectionTitle>Export Line Items</SectionTitle>
                <Row className="g-2 align-items-end">
                  <Col xs={12} sm={6} md={3}>
                    <InputDatePicker
                      column="fromDate"
                      displayName="From"
                      selected={fromDate}
                      handleChange={handleDateChange}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={3}>
                    <InputDatePicker
                      column="toDate"
                      displayName="To"
                      selected={toDate}
                      handleChange={handleDateChange}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={2}>
                    <Button onClick={handleExport} disabled={isExportDisabled} className="w-100">
                      {exporting ? 'Exporting...' : 'Export'}
                    </Button>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default SupplierOtif;
